import {
  EligibleNotification,
  NotificationAction,
  NotificationLocalizedContent,
} from "@/types/notificationTypes";

const ALWAYS_EXCLUDED_PATH_PREFIXES = [
  "/login",
  "/reset-password",
  "/print",
  "/test-result",
  "/filtered-test-result",
];

const DEFAULT_IMAGE_HOSTS = ["oculera-marketing.s3.eu-central-1.amazonaws.com"];

function normalizeLocale(locale: string | null | undefined): string {
  return (locale || "en").trim().toLowerCase().replaceAll("_", "-");
}

export function getLocalizedNotificationContent(
  contentByLocale: EligibleNotification["contentByLocale"],
  locale: string | null | undefined,
): NotificationLocalizedContent | null {
  const normalizedLocale = normalizeLocale(locale);
  const baseLocale = normalizedLocale.split("-")[0];
  const normalizedContentEntries = Object.entries(contentByLocale ?? {}).map(
    ([contentLocale, content]) => [normalizeLocale(contentLocale), content] as const,
  );
  const findLocale = (targetLocale: string) =>
    normalizedContentEntries.find(([contentLocale]) => contentLocale === targetLocale)?.[1];
  const findEnglishVariant = () =>
    normalizedContentEntries.find(([contentLocale]) => contentLocale.startsWith("en-"))?.[1];
  return (
    findLocale(normalizedLocale) ??
    findLocale(baseLocale) ??
    findLocale("en") ??
    findEnglishVariant() ??
    null
  );
}

export function isGloballyExcludedNotificationPath(pathname: string): boolean {
  return ALWAYS_EXCLUDED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function getSafeInternalUrl(url: string | null | undefined): string | null {
  if (!url || !url.startsWith("/") || url.startsWith("//")) return null;
  try {
    const origin =
      typeof window === "undefined" ? "https://app.oculera.invalid" : window.location.origin;
    const parsed = new URL(url, origin);
    return parsed.origin === origin ? `${parsed.pathname}${parsed.search}${parsed.hash}` : null;
  } catch {
    return null;
  }
}

export function getSafeExternalUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" ? parsed.toString() : null;
  } catch {
    return null;
  }
}

function getApprovedImageHosts(): Set<string> {
  const configuredHosts = (process.env.REACT_APP_NOTIFICATION_IMAGE_HOSTS || "")
    .split(",")
    .map((host) => host.trim().toLowerCase())
    .filter(Boolean);
  return new Set([...DEFAULT_IMAGE_HOSTS, ...configuredHosts]);
}

export function getSafeNotificationImageUrl(url: string | null | undefined): string | null {
  const safeUrl = getSafeExternalUrl(url);
  if (!safeUrl) return null;
  const parsed = new URL(safeUrl);
  return getApprovedImageHosts().has(parsed.hostname.toLowerCase()) ? safeUrl : null;
}

export function isRenderableNotificationAction(action: NotificationAction): boolean {
  if (!action?.id) return false;
  if (action.type === "internal_link") return Boolean(getSafeInternalUrl(action.url));
  if (action.type === "external_link") return Boolean(getSafeExternalUrl(action.url));
  return action.type === "acknowledge" || action.type === "snooze";
}
